import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useCreateEvaluation,
  useDeleteEvaluation,
  useDevelopmentConfig,
  useEvaluation,
  useEvaluations,
  usePlayers,
  useUpdateEvaluation,
  type EvaluationInputBody,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate, sheetName } from '@/lib/format';
import { configLabel } from '@/lib/labels';
import { todayISO } from '@shared/dates';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Sheet, SheetHead, SheetRow } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';

const DIMENSIONS = [
  'technical',
  'tactical',
  'physical',
  'mental',
  'social',
  'goalkeeping',
] as const;

const READINESS = [
  'not_ready',
  'developing',
  'close',
  'ready_for_exposure',
  'ready',
] as const;

type ScoreMap = Record<string, number | null>;

/** The averages a list row shows, in a fixed order so rows scan vertically. */
function AverageChips({
  averages,
  t,
}: {
  averages: Record<string, number>;
  t: (key: string) => string;
}) {
  const shown = DIMENSIONS.filter((d) => averages[d] !== undefined);
  if (shown.length === 0) return <span className="text-meta text-ink-faint">{t('evaluation.noScores')}</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {shown.map((d) => (
        <span
          key={d}
          className="tabular inline-flex items-center gap-1 rounded-[3px] bg-surface-sunken px-1.5 py-0.5 text-meta text-ink-muted"
          title={t(`development.dimension.${d}`)}
        >
          <span className="font-condensed uppercase">{t(`development.dimension.${d}`).slice(0, 3)}</span>
          <span className="font-semibold text-ink">{averages[d]!.toFixed(1)}</span>
        </span>
      ))}
    </span>
  );
}

export function Evaluations({ locale, teamId }: { locale: Locale; teamId: string }) {
  const t = useTranslations();
  const config = useDevelopmentConfig();
  const players = usePlayers(teamId);
  const list = useEvaluations({ teamId });
  const create = useCreateEvaluation();
  const update = useUpdateEvaluation();
  const remove = useDeleteEvaluation();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const loaded = useEvaluation(editingId);

  const [playerId, setPlayerId] = useState('');
  const [onDate, setOnDate] = useState(todayISO());
  const [scaleKey, setScaleKey] = useState('');
  const [context, setContext] = useState('');
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const [summary, setSummary] = useState('');
  const [readiness, setReadiness] = useState('');
  const [readinessNote, setReadinessNote] = useState('');
  const [scores, setScores] = useState<ScoreMap>({});
  const [showGk, setShowGk] = useState(false);

  const criteria = config.data?.criteria ?? [];
  const scales = config.data?.scales ?? [];
  const scale = scales.find((s) => s.key === scaleKey) ?? scales[0];
  const squad = players.data ?? [];
  const chosen = squad.find((p) => p.id === playerId);
  // A goalkeeper is rated on the goalkeeping set; an outfield player is not asked
  // 15 questions about handling crosses.
  const isKeeper = chosen?.primaryPositionKey === 'GK' || chosen?.secondaryPositionKey === 'GK';

  useEffect(() => {
    if (!editingId || !loaded.data) return;
    const e = loaded.data.evaluation;
    setPlayerId(e.playerId);
    setOnDate(e.onDate);
    setScaleKey(e.scaleKey ?? '');
    setContext(e.context ?? '');
    setStrengths(e.strengths ?? '');
    setWeaknesses(e.weaknesses ?? '');
    setSummary(e.summary ?? '');
    setReadiness(e.seniorReadiness ?? '');
    setReadinessNote(e.seniorReadinessNote ?? '');
    const next: ScoreMap = {};
    for (const s of loaded.data.scores) next[s.criterionKey] = s.score;
    setScores(next);
    setShowGk(loaded.data.scores.some((s) => s.dimension === 'goalkeeping' && s.score !== null));
  }, [editingId, loaded.data]);

  const startCreate = () => {
    setEditingId(null);
    setPlayerId('');
    setOnDate(todayISO());
    setScaleKey(scales.find((s) => s.isDefault)?.key ?? scales[0]?.key ?? '');
    setContext('');
    setStrengths('');
    setWeaknesses('');
    setSummary('');
    setReadiness('');
    setReadinessNote('');
    setScores({});
    setShowGk(false);
    setOpen(true);
  };

  const byDimension = useMemo(() => {
    const out = new Map<string, typeof criteria>();
    for (const c of criteria) {
      const group = out.get(c.dimension) ?? [];
      group.push(c);
      out.set(c.dimension, group);
    }
    return out;
  }, [criteria]);

  const save = async () => {
    const body: EvaluationInputBody = {
      playerId,
      teamId,
      onDate,
      scaleKey: scaleKey || null,
      context: context || null,
      strengths: strengths || null,
      weaknesses: weaknesses || null,
      summary: summary || null,
      seniorReadiness: readiness || null,
      seniorReadinessNote: readinessNote || null,
      // Every configured criterion is submitted, scored or not: the sheet is the
      // record, and a criterion the coach cleared has to come back cleared.
      scores: criteria
        .filter((c) => c.dimension !== 'goalkeeping' || isKeeper || showGk)
        .map((c) => ({
          criterionKey: c.key,
          dimension: c.dimension,
          score: scores[c.key] ?? null,
        })),
    };
    const ok = await submit(async () => {
      if (editingId) {
        const { playerId: _drop, ...rest } = body;
        await update.mutateAsync({ id: editingId, input: rest });
      } else {
        await create.mutateAsync(body);
      }
    });
    if (ok) {
      setOpen(false);
      setEditingId(null);
    }
  };

  const rows = list.data ?? [];

  return (
    <>
      {open ? (
        <div className="mb-5">
          <Panel
            title={editingId ? t('evaluation.edit') : t('evaluation.add')}
            action={
              <Button variant="quiet" onClick={() => { setOpen(false); setEditingId(null); }}>
                {t('common.cancel')}
              </Button>
            }
          >
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              {formError ? (
                <div className="px-4 pt-4">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Section title={t('evaluation.about')}>
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
                  <Field label={t('evaluation.player')} error={fieldError('playerId')}>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={playerId}
                        onChange={(e) => setPlayerId(e.target.value)}
                        required
                        disabled={Boolean(editingId)}
                      >
                        <option value="">{t('evaluation.pickPlayer')}</option>
                        {squad.map((pl) => (
                          <option key={pl.id} value={pl.id}>
                            {sheetName(pl.knownAs ?? pl.firstName, pl.lastName)}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                  </Field>

                  <Field label={t('evaluation.date')} error={fieldError('onDate')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="date"
                        value={onDate}
                        onChange={(e) => setOnDate(e.target.value)}
                        required
                      />
                    )}
                  </Field>

                  <Field label={t('evaluation.scale')} optional>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={scaleKey}
                        onChange={(e) => setScaleKey(e.target.value)}
                      >
                        {scales.map((s) => (
                          <option key={s.key} value={s.key}>
                            {configLabel(s, t)}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                  </Field>

                  <Field label={t('evaluation.context')} optional hint={t('evaluation.contextHint')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </Section>

              <Section title={t('evaluation.ratings')} hint={t('evaluation.ratingsHint')}>
                <div className="grid gap-5">
                  {DIMENSIONS.filter(
                    (d) => d !== 'goalkeeping' || isKeeper || showGk,
                  ).map((dimension) => {
                    const group = byDimension.get(dimension) ?? [];
                    if (group.length === 0) return null;
                    return (
                      <fieldset key={dimension}>
                        <legend className="mb-2 font-condensed text-meta font-semibold uppercase tracking-wide text-ink-faint">
                          {t(`development.dimension.${dimension}`)}
                        </legend>
                        <div className="grid gap-x-5 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
                          {group.map((c) => (
                            <label
                              key={c.key}
                              className="flex items-center justify-between gap-3 border-b border-line py-1.5"
                            >
                              <span className="min-w-0 flex-1 truncate text-meta text-ink-muted">
                                {configLabel(c, t)}
                              </span>
                              <SelectInput
                                className="w-16 shrink-0"
                                value={scores[c.key] === null || scores[c.key] === undefined ? '' : String(scores[c.key])}
                                onChange={(e) =>
                                  setScores((s) => ({
                                    ...s,
                                    [c.key]: e.target.value === '' ? null : Number(e.target.value),
                                  }))
                                }
                              >
                                <option value="">{t('evaluation.blank')}</option>
                                {Array.from(
                                  { length: (scale?.max ?? 5) - (scale?.min ?? 1) + 1 },
                                  (_, i) => (scale?.min ?? 1) + i,
                                ).map((n) => (
                                  <option key={n} value={n}>
                                    {scale?.labels?.[n - (scale.min ?? 1)] ?? n}
                                  </option>
                                ))}
                              </SelectInput>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    );
                  })}

                  {!isKeeper ? (
                    <label className="flex items-center gap-2 text-meta text-ink-muted">
                      <input
                        type="checkbox"
                        checked={showGk}
                        onChange={(e) => setShowGk(e.target.checked)}
                        className="size-4 accent-ink"
                      />
                      {t('evaluation.showGk')}
                    </label>
                  ) : null}
                </div>
              </Section>

              <Section title={t('evaluation.summary')}>
                <div className="grid gap-x-5 gap-y-2 lg:grid-cols-3">
                  <Field label={t('evaluation.strengths')} optional>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={3}
                        value={strengths}
                        onChange={(e) => setStrengths(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={t('evaluation.weaknesses')} optional>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={3}
                        value={weaknesses}
                        onChange={(e) => setWeaknesses(e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={t('evaluation.summary')} optional>
                    {(p) => (
                      <TextArea
                        {...p}
                        rows={3}
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </Section>

              <Section title={t('development.readiness')}>
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-3">
                  <Field label={t('development.readiness')} optional>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={readiness}
                        onChange={(e) => setReadiness(e.target.value)}
                      >
                        <option value="">{t('development.readinessValue.unset')}</option>
                        {READINESS.map((r) => (
                          <option key={r} value={r}>
                            {t(`development.readinessValue.${r}`)}
                          </option>
                        ))}
                      </SelectInput>
                    )}
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label={t('development.readinessNote')} optional>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={readinessNote}
                          onChange={(e) => setReadinessNote(e.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </Section>

              <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-4">
                <Button type="submit" variant="primary" busy={busy} disabled={!playerId}>
                  {t('evaluation.save')}
                </Button>
                {editingId ? (
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (!window.confirm(t('evaluation.confirmDelete'))) return;
                      void remove.mutateAsync(editingId).then(() => {
                        setOpen(false);
                        setEditingId(null);
                      });
                    }}
                  >
                    {t('evaluation.delete')}
                  </Button>
                ) : null}
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      <Panel
        title={t('evaluation.title')}
        count={rows.length}
        action={
          open ? null : (
            <Button variant="primary" onClick={startCreate}>
              {t('evaluation.add')}
            </Button>
          )
        }
      >
        {list.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-6 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('evaluation.empty')}
            body={t('evaluation.emptyBody')}
            action={
              <Button variant="primary" onClick={startCreate}>
                {t('evaluation.add')}
              </Button>
            }
          />
        ) : (
          <>
            <SheetHead>
              <span className="w-24 shrink-0">{t('evaluation.date')}</span>
              <span className="flex-1">{t('evaluation.player')}</span>
              <span className="hidden flex-1 md:block">{t('evaluation.average')}</span>
              <span className="w-28 shrink-0 text-end">{t('development.readiness')}</span>
            </SheetHead>
            <Sheet>
              {rows.map((r) => (
                <SheetRow
                  key={r.id}
                  // The row opens the evaluation and the action opens the
                  // player's file. Before, the row did nothing and the two jobs
                  // were a 25px name and a 20px link inside 48px of dead space.
                  onClick={() => {
                    setEditingId(r.id);
                    setOpen(true);
                  }}
                  action={
                    <Link
                      to={localePath(locale, `/development/players/${r.playerId}`)}
                      className="inline-flex min-h-11 items-center px-1 text-meta text-ink-faint underline-offset-2 hover:text-ink hover:underline"
                    >
                      {t('development.openFile')}
                    </Link>
                  }
                >
                  <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                    {formatDate(r.onDate, locale)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">
                      {sheetName(r.knownAs ?? r.firstName, r.lastName)}
                    </span>
                    <span className="block truncate text-meta text-ink-faint">
                      {r.context ?? t('evaluation.complete', {
                        percent: Math.round(r.completeness * 100),
                      })}
                    </span>
                    <span className="mt-1 flex md:hidden">
                      <AverageChips averages={r.averages} t={t} />
                    </span>
                  </span>
                  <span className="hidden flex-1 md:block">
                    <AverageChips averages={r.averages} t={t} />
                  </span>
                  <span className="flex w-28 shrink-0 justify-end">
                    {r.seniorReadiness ? (
                      <Chip muted>{t(`development.readinessValue.${r.seniorReadiness}`)}</Chip>
                    ) : null}
                  </span>
                </SheetRow>
              ))}
            </Sheet>
          </>
        )}
      </Panel>
    </>
  );
}
